import { useState } from 'react';
import { IconCircleOff, IconDownload, IconX, IconCursorText } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Button, Modal, useToast } from '@hana/ui';
import { INV_CUS, ISS_ROWS } from './inv';
import { MI_ROWS, VAT_PERIODS, miApply, miResult, miSummary, vatRows } from './invf';
import s from './invm2.module.css';
import './misa.i18n';

const f1 = (x: number): string => x.toFixed(1).replace('.', ',');
export type MisaKind = 'mx' | 'mi' | 'vat' | null;

// Mint-Invoice › MODAL "Xuất template MISA meInvoice" / "Nhập bảng kê hoá đơn từ MISA" / "Báo cáo VAT bán ra". Chỉ đọc: nút ghi / tải báo toast.
export function MisaModals({ kind, money, onClose }: { kind: MisaKind; money: boolean; onClose: () => void }) {
  if (kind === 'mx') return <Mx onClose={onClose} />;
  if (kind === 'mi') return <Mi onClose={onClose} />;
  if (kind === 'vat') return <Vat money={money} onClose={onClose} />;
  return null;
}

function Mx({ onClose }: { onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const [off, setOff] = useState<readonly string[]>([]);
  const n = ISS_ROWS.length - off.length;
  const go = () => { if (!n) return; onClose(); toast({ msg: t('ms.mxOff') }); };
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('ms.mxTitle')}
      footer={<><Button icon={<IconCircleOff size={16} aria-hidden="true" />} onClick={onClose}>{t('ms.cancel')}</Button><Button variant="primary" disabled={!n} icon={<IconDownload size={16} aria-hidden="true" />} onClick={go}>{t('ms.download', { n })}</Button></>}
    >
      <div className={s.list}>
        {ISS_ROWS.map((r) => (
          <label key={r.dn} className={s.row}>
            <input type="checkbox" checked={!off.includes(r.dn)} onChange={() => setOff(off.includes(r.dn) ? off.filter((x) => x !== r.dn) : [...off, r.dn])} />
            <b>{r.dn}</b><span>{INV_CUS[r.cus]?.[0]}</span><em>{f1(r.net + r.vat)} {t('ms.tr')}</em>
          </label>
        ))}
      </div>
    </Modal>
  );
}

function Mi({ onClose }: { onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [over, setOver] = useState(false);
  const n = miApply(over);
  const go = () => { onClose(); toast({ msg: t('ms.miOff') }); };
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('ms.miTitle')}
      footer={
        <>
          {step === 1 && <Button icon={<IconDownload size={16} aria-hidden="true" />} onClick={() => toast({ msg: t('ms.errOff') })}>{t('ms.errRows')}</Button>}
          <span className={s.grow} />
          <Button icon={<IconCircleOff size={16} aria-hidden="true" />} onClick={onClose}>{t('ms.cancel')}</Button>
          {step === 1 && <Button variant="primary" onClick={go}>{t('ms.write', { n })}</Button>}
        </>
      }
    >
      {step === 0 ? (
        <button type="button" className={s.drop} onClick={() => setStep(1)}>
          <span><IconCursorText size={16} aria-hidden="true" />{t('ms.pick')}</span><small>{t('ms.drag')}</small>
        </button>
      ) : (
        <div className={s.mi}>
          <div className={s.lb}>BangKe_HDDT_22-10-2026.xlsx · {miSummary(over)}</div>
          <div className={s.tbl}>
            <div className={`${s.mr} ${s.mh}`}><span>{t('ms.hNote')}</span><span>{t('ms.hSym')}</span><span>{t('ms.hNo')}</span><span>{t('ms.hDate')}</span><span>{t('ms.hRes')}</span></div>
            {MI_ROWS.map(([dn, sym, no, d, r]) => { const x = miResult(r, over); return <div key={dn} className={s.mr}><b>{dn}</b><span>{sym}</span><span>{no}</span><span>{d}</span><span className={s[x.tone]}>{x.label}</span></div>; })}
          </div>
          <label className={s.chk}><input type="checkbox" checked={over} onChange={() => setOver(!over)} />{t('ms.overwrite')}</label>
        </div>
      )}
    </Modal>
  );
}

function Vat({ money, onClose }: { money: boolean; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const [p, setP] = useState<string>(VAT_PERIODS[0]);
  const m = (x: string) => (money ? x : '•••••');
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('ms.vatTitle')}
      description={t('ms.vatDesc')}
      footer={<><Button icon={<IconX size={16} aria-hidden="true" />} onClick={onClose}>{t('ms.close')}</Button><Button variant="primary" icon={<IconDownload size={16} aria-hidden="true" />} onClick={() => toast({ msg: t('ms.vatOff') })}>{t('ms.csv')}</Button></>}
    >
      <div className={s.mi}>
        <label className={s.fld}><span>{t('ms.period')}</span><select value={p} onChange={(e) => setP(e.target.value)}>{VAT_PERIODS.map((x) => <option key={x}>{x}</option>)}</select></label>
        <div className={s.tbl}>
          <div className={`${s.vr} ${s.mh}`}><span>{t('ms.hRate')}</span><span>{t('ms.hCount')}</span><span>{t('ms.hNet')}</span><span>{t('ms.hVat')}</span></div>
          {vatRows(p).map((v) => <div key={v.k} className={`${s.vr} ${v.total ? s.tot : ''}`}><span>{v.k}</span><span>{v.n}</span><span>{m(f1(v.net))}</span><span>{m(f1(v.vat))}</span></div>)}
        </div>
      </div>
    </Modal>
  );
}
